// Constants for coverage dropdowns
// Merged from constants.ts and coverageConstants.ts
export const STANDARD_WAITING_PERIOD_VALUES = [10, 20, 30, 65, 130, 260];
export const EMPLOYER_COST_OPTIONS = [0, 5, 10, 15, 20, 25, 30]; // Using the more complete version
export const COVERAGE_OPTIONS = [70, 75, 80, 85, 90, 95, 100];

// Coverage field names for comparison and normalization
export const COVERAGE_FIELDS = [
	'waiting_period',
	'employer_costs',
	'coverage_percentage_first_year_first_half',
	'coverage_percentage_first_year_second_half',
	'coverage_percentage_second_year_first_half',
	'coverage_percentage_second_year_second_half'
] as const;

export type CoverageField = (typeof COVERAGE_FIELDS)[number];

// Default coverage values
export const DEFAULT_COVERAGE_VALUES = {
	waiting_period: 30,
	employer_costs: 0,
	coverage_percentage_first_year_first_half: 100,
	coverage_percentage_first_year_second_half: 100,
	coverage_percentage_second_year_first_half: 70,
	coverage_percentage_second_year_second_half: 70
} as const;

/**
 * Normalizes coverage values from API/current_policy to CoverageValues type.
 * Handles null/undefined values and converts strings to numbers.
 */
export const normalizeCoverageValues = (
	coverage?: Partial<Record<CoverageField, number | string | null>>
): Record<CoverageField, number> => {
	const result = {} as Record<CoverageField, number>;
	for (const field of COVERAGE_FIELDS) {
		result[field] = Number(coverage?.[field] ?? DEFAULT_COVERAGE_VALUES[field]);
	}
	return result;
};

/**
 * True when the `current_policy.sick_leave_short_term.coverage` block carries
 * real coverage data worth comparing against. In the no-policy flow the backend
 * leaves `coverage` null, so callers pass an empty object here and get `false`.
 * `employer_costs` is excluded because it defaults to 0 even on an otherwise
 * empty coverage block, so it alone cannot signal a real policy.
 */
export const hasRealCurrentPolicy = (
	coverage?: Partial<Record<CoverageField, number | string | null>> | null
): boolean => {
	if (!coverage) return false;
	return COVERAGE_FIELDS.some((field) => field !== 'employer_costs' && coverage[field] != null);
};
